import { Card, Typography, TextField, Divider, FormGroup, MenuItem, Stack, Grid, Badge, IconButton, Tooltip, Box } from "@mui/material";
import { useGetMainList, useGetOne, useRecordContext } from "ra-core";
import { useEffect, ChangeEvent, FC, useCallback, useMemo } from "react";
import { useHistory } from 'react-router-dom'
import { RecPatch } from "~/dataProvider/tools";
import { EncryptionMappingResponseDto, TableSchemaResponseDto } from "~/gen/api/governance";
import { useMutateEncryptionMappingsContext } from "~/pages/obfuscation/mutateEncryptionMappingsContext";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { BasicInfoRow, BasicInfoPiece } from "../../backups/BackupBasicInfoCard";
import MappedIcon from "~/icons/Mapped";
import SkippedIcon from "~/icons/Skipped";
import PendingReviewIcon from "~/icons/PendingReview";
import CopyTableMappings from "./CopyTableMappings";



export const TableSelectionCard: FC<{ useSource: 'tables'|'pending-tables' }> = ({ useSource }) => {

  const record = useRecordContext<EncryptionMappingResponseDto & RecPatch>()
  const { data: dbTablesMap, refetch: tbSelectRefetch } = 
    useGetMainList<TableSchemaResponseDto & RecPatch>(!(record?.databaseId) ? 'noop' : useSource, pages(), sorts(), { dbId: record?.databaseId })
  const dbTables = useMemo(() => 
    Object.values(dbTablesMap || {})
      .sort((a, b) => a.schema === b.schema 
        ? ((a.name || '') < (b.name || '') ? -1 : 1)
        : ((a.schema || '') < (b.schema || '') ? -1 : 1)), [dbTablesMap])
  
  const { currTable, setCurrTable, mappingAmend } = useMutateEncryptionMappingsContext()
  const { data: fieldsCountsData } = useGetOne<FieldsCounts & RecPatch>(currTable ? 'db-fields-mapping-count' : 'noop', currTable?.databaseId || -1)
  const fieldsCounts: FieldsCounts = fieldsCountsData || {}
  
  const handleTableChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const selectedTable = dbTables.find(x => x.tableId === +e.target.value)
    setCurrTable(selectedTable)
  }, [dbTables])
  
  const history = useHistory()
  useEffect(() => {
    if (!currTable && dbTables.length > 0) {
      setCurrTable(dbTables[0])
    }
    if (currTable) {
      if (dbTables.length === 0) {
        history.goBack()
      } else {
        const tb = dbTables.find(d => d.id === currTable.tableId) ?? dbTables[0]
        setCurrTable(tb)
      }
    }
  }, [currTable, setCurrTable, dbTables.length, history])

  return useMemo(() => (
    <Card>
      <BasicInfoRow>
        <BasicInfoPiece>
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Database
          </Typography>
          <Typography
            color="textSecondary"
            variant="body1"
          >
            {record?.databaseName}
          </Typography>
        </BasicInfoPiece>
        <BasicInfoPiece>
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Engine
          </Typography>
          <Typography
            color="textSecondary"
            variant="body1"
          >
            {record?.engineVersion + ' ' + (record?.engineEdition || '')}
          </Typography>
        </BasicInfoPiece>
      </BasicInfoRow>
      <Divider />
      <Grid
        container
        sx={{
          px: 3,
          py: 1,
          alignItems: 'center',
        }}
      >
        <Grid item xs={8}>
          <Grid container>
            <Grid item xs={3}>            
              <Typography
                id="table-name"
                color="textPrimary"
                variant="h6"
                sx={{ flex: 1 }}
              >
                Table Name
              </Typography>
            </Grid>            
            <Grid item xs={9} sx={listSx}>
              <FormGroup>
                <TextField
                  value={currTable?.tableId || dbTables[0]?.tableId || ''}
                  onChange={handleTableChange}
                  select
                  size="small"
                  id='table-select'
                  SelectProps={{
                    inputProps: {
                      labelId: 'table-name',
                    },
                    MenuProps: { PaperProps: { sx: listSx }, keepMounted: true },
                  }}
                >
                  {dbTables.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      <Box className="gcontainer"
                        sx={{ 
                          width: '100%',
                          '& span.MuiTypography-body2': { 
                            maxWidth: 320,
                            wordBreak: 'keep-all',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mr: 'auto',
                          }
                        }}
                      >
                        <Typography component="span" variant="caption" color="InactiveCaptionText"
                          className="i1"
                        >
                          {t.schema}.
                        </Typography>
                        <Typography component="span" variant="body2" 
                          className="i2"                        
                        >
                          {t.name}
                        </Typography>
                        <Typography component="span" variant="caption" color="success.main"
                          className="i3"
                        >
                          <Badge 
                            color={"success" as any} 
                            badgeContent={t.mappedCount} 
                            // showZero 
                            max={9999} 
                          />
                        </Typography>
                        <Typography component="span" variant="caption"
                          className="i4"
                          sx={{ color: 'warning.light', mr: 2 }}
                        >
                          <Badge 
                            color={"warning" as any} 
                            badgeContent={t.id === currTable?.tableId 
                              ? ((t.pendingReviewCount || 0) + mappingAmend["pending-review"]) 
                              : (t.pendingReviewCount || 0)} 
                            // showZero 
                            max={9999} 
                            sx={{ 'span.MuiBadge-badge': {color: theme => theme.palette.mode === 'light' ? 'black!important' : undefined }}}
                          />
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </FormGroup>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex' }}>
          {record && currTable && 
            <CopyTableMappings destDatabaseName={record.databaseName!} destTable={currTable} tbSelectRefetch={tbSelectRefetch}/>}
          <Stack 
            direction="row" 
            spacing={4} 
            sx={{ 
              mx: 'auto',
              '& span.MuiBadge-badge': { whiteSpace: 'nowrap' },
            }} 
          >
            <Tooltip title="Mapped">
              <IconButton color={"inherit" as any} size="small">
                <Badge 
                  color={"success" as any} 
                  badgeContent={'' + 
                    (fieldsCounts.mappedCount || 0) + 
                    (mappingAmend.mapped !== 0 
                      ? ' ' + toSignedString(mappingAmend.mapped)
                      : '')}
                  showZero 
                  max={9999}
                >
                  <MappedIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Skipped">
              <IconButton color={"inherit"} size="small">
                <Badge 
                  color="secondary" 
                  badgeContent={'' + 
                    (fieldsCounts.skippedCount || 0) + 
                    (mappingAmend.skip !== 0 
                      ? ' ' + toSignedString(mappingAmend.skip)
                      : '')}
                  showZero 
                  max={9999}
                >
                  <SkippedIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Pending Review">
              <IconButton color={"inherit" as any} size="small">
                <Badge 
                  color={"warning" as any} 
                  badgeContent={'' + 
                    (fieldsCounts.pendingReviewCount || 0) + 
                    (mappingAmend["pending-review"] !== 0 
                      ? ' ' + toSignedString(mappingAmend["pending-review"])
                      : '')}
                  showZero 
                  max={9999}
                  sx={{ 'span.MuiBadge-badge': {color: theme => theme.palette.mode === 'light' ? 'black!important' : undefined }}}
                >
                  <PendingReviewIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>
    </Card>
  ), [currTable, dbTables, fieldsCountsData, mappingAmend["mapped"], mappingAmend["skip"], mappingAmend["pending-review"], ])
}

const listSx = {
  '& .gcontainer': {                          
    display: 'grid',
    gridAutoColumns: 'minmax(50px, auto) 1fr minmax(20px, auto) minmax(20px, auto)',
    columnGap: 0.5,
    '& .i1': {
      lineHeight: '1.8',
      textAlign: 'right',
      gridColumn: '1 / span 1'
    },
    '& .i2': { gridColumn: '2 / span 1' },
    '& .i3': { gridColumn: '3 / span 1' },
    '& .i4': { gridColumn: '4 / span 1' }
  }
}

const toSignedString = (x:number) => (Math.abs(x) === x ? '+' : '-') + Math.abs(x)

interface FieldsCounts {
  mappedCount?: number;
  skippedCount?: number;
  pendingReviewCount?: number;
}
